import {
  useBonusConfigurationMutation,
  useBonusConfigurationQuery,
} from '@/lib/api/company/timesheet/bonusConfiguration'
import ModalLayout from '../shared/ModalLayout'
import * as React from 'react'
import { Button, Checkbox, Loader } from '@/ui'
import { FieldsetInput } from '@/modules/settings/components/FieldsetInput'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import { FieldsetSwitch } from '@/modules/settings/components/FieldsetSwitch'
import { BonusConfigurationModel } from '@/app/models/timesheet/bonusConfigurationModel'
import { RatesOption } from '@/app/constants/ratesOption'
import { getRatesOptionName } from '@/app/common/converters/ratesOptionToTextConverter'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import axios from 'axios'
import { Table, TableCell, TableRow } from '@/ui/data-display/table-flex'
import { getDayTypeName } from '@/app/common/converters/timesheet/dayTypeToTextConverter'
import {
  TABLE_ALLOWANCES_COLSPAN,
  getAllowancesTableConfig,
  TABLE_ALLOWANCES_CHECKBOX_KEY,
  TABLE_ALLOWANCES_CHECKBOX_NAME,
} from './allowance-table-config'

import { getTimesheetAdditionalBonusName } from '@/app/common/converters/timesheet/timesheetAdditionalBonus'
import { mapTimesheetAdditionalBonusApiToEnum } from '@/app/common/mappers/timesheetAdditionalBonus'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { useDirtyStore } from '@/modules/shared/stores/dirtyStore'
import { TimesheetAdditionForLeaveModel } from '@/app/models/timesheet/groupedTimesheetModel'
import { faClose, FaIcon } from '@/lib/fa'

type Props = {
  companyId: string
  driverId: string
  month: number
  year: number
  handleClose?: () => void
}

const BONUS_RATES_OPTIONS = [
  RatesOption.ConstantValue,
  RatesOption.PercentFromAmount,
  RatesOption.PercentFromAmountAndLumpSum,
]

export const DriverBonusAndRewardsModal = ({
  driverId,
  companyId,
  month,
  year,
  handleClose,
}: Props) => {
  const wrapConfirm = useWrapConfirm()

  const query = useBonusConfigurationQuery({
    companyId,
    disabled: false,
    payload: {
      companyId,
      driverId,
      month,
      year,
    },
  })

  const mutation = useBonusConfigurationMutation({
    onSuccess: () => {
      toast.success({
        text: t('common.data-updated'),
      })
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        const title = (err.response?.data as any)?.title

        if (title) {
          toast.error({ text: title })
          return
        }
      }
      toast.error({
        text: t('common.save-error'),
      })
    },
    companyId,
    payload: {
      companyId,
      driverId,
      month,
      year,
    },
  })

  const [state, setState] =
    React.useState<Partial<BonusConfigurationModel> | null>(null)

  const { t } = useLocalize()

  React.useEffect(() => {
    setState(query.data)
  }, [query.data])

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      switch (name) {
        case 'rewardRate':
        case 'bonusRate':
        case 'rewardRateFromContract':
        case 'bonusRateFromContract':
          setState((state) => ({
            ...state,
            [name]: Number(value),
          }))
          break
        case 'finalLeavesGrossAmount':
          setState((state) => ({
            ...state,
            finalLeavesGrossAmount: value === '' ? undefined : Number(value),
          }))
          break
        case 'finalLeavesGrossHours':
          setState((state) => ({
            ...state,
            finalLeavesGrossHours: value === '' ? undefined : Number(value),
          }))
          break
        default:
          return
      }
    },
    [],
  )

  const timesheetAdditionForLeaves = React.useMemo(() => {
    return state &&
      state.timesheetAdditionForLeaves &&
      Array.isArray(state.timesheetAdditionForLeaves)
      ? state.timesheetAdditionForLeaves
      : []
  }, [state])

  const handleChangeAddition = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name } = e.target
      const value = Number(e.target.value)
      setState((state) => {
        const current = Array.isArray(state?.timesheetAdditionForLeaves)
          ? state.timesheetAdditionForLeaves
          : []
        const newTimesheetAdditionForLeaves: TimesheetAdditionForLeaveModel[] =
          current.map((a) =>
            String(a.dayType) === name
              ? { ...a, amount: value, isAutoGeneratedAddition: false }
              : a,
          )
        return {
          ...state,
          timesheetAdditionForLeaves: newTimesheetAdditionForLeaves,
        }
      })
    },
    [],
  )

  const handleSelect = React.useCallback((value: string, props) => {
    const { name, option } = props
    switch (name) {
      case 'bonusRateOption':
      case 'bonusRateFromContractOption':
        setState((state) => ({
          ...state,
          [name]: option.key,
        }))
        break
      default:
        return
    }
  }, [])

  const toggleUseValuesFromContract = React.useCallback(() => {
    setState((state) => ({
      ...state,
      useValuesFromContract: !state.useValuesFromContract,
    }))
  }, [])

  const handleCancel = React.useCallback(() => {
    setState(query.data)
  }, [query.data])

  const handleSubmit = () => {
    if (!state) return

    const {
      leavesGrossAmount: _omitLeavesGross,
      sickLeavesGrossAmount: _omitSickGross,
      leavesNettoAmount: _omitLeavesNetto,
      sickLeavesNettoAmount: _omitSickNetto,
      ...payload
    } = state
    mutation.mutateAsync(payload)
  }

  const isDirty =
    state != null &&
    query.data != null &&
    JSON.stringify(state) !== JSON.stringify(query.data)

  const setDirty = useDirtyStore((s) => s.setDirty)
  React.useEffect(() => {
    setDirty(isDirty)
    return () => setDirty(false)
  }, [setDirty, isDirty])

  const isSaving = mutation.isPending

  const allowancesConfig = getAllowancesTableConfig(t, {
    useValuesFromContract: state?.useValuesFromContract || false,
  })

  const handleChangeAllowances = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked, dataset } = e.currentTarget
    const { index, bonusType } = dataset

    if (type === 'checkbox') {
      setState((state) => ({
        ...structuredClone(state),
        [name]: checked,
      }))
      return
    }

    if (!index) {
      return
    }

    setState((state) => {
      const newState = structuredClone(state)
      newState.additionalBonuses = structuredClone(state.additionalBonuses)
      const rowIndex = Number(index)
      if (rowIndex >= 0) {
        newState.additionalBonuses[rowIndex][name] =
          type === 'number' ? Number(value) : value
      } else {
      }

      return newState
    })
  }

  return (
    <ModalLayout>
      <div className="tw-flex tw-flex-col tw-rounded-md tw-bg-white tw-p-4">
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-pb-2 tw-border-base-300">
          <div className="tw-flex tw-items-center tw-gap-4">
            <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
              {t('activities.add-new-activity')}
            </p>
            {/* <div
              key={driverName}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-base-300 tw-px-1 tw-py-1"
            >
              <FaIcon
                icon={faUserSolid}
                className="tw-h-3 tw-w-3"
                onClick={() => close()}
              />
              <p className="tw-text-xs tw-text-content-base">{driverName}</p>
            </div> */}
          </div>
          <FaIcon
            icon={faClose}
            className="tw-mr-4 tw-h-5 tw-w-5 tw-cursor-pointer tw-text-content-base hover:tw-text-primary"
            onClick={() => handleClose()}
          />
        </div>
        <div className="tw-relative tw-pt-2">
          {query.isError ? (
            <p>{t('common.server-fetch-error')}</p>
          ) : query.isFetching ? (
            <Loader />
          ) : state ? (
            <div className="tw-flex tw-flex-col tw-gap-4">
              {isSaving && (
                <Loader
                  position="absolute"
                  shadow
                  className="tw-z-50 tw-h-full tw-w-full"
                />
              )}
              <div className="tw-relative tw-z-10 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                <FieldsetInput
                  label={t('timesheet.bonus')}
                  type="number"
                  step={0.01}
                  placeholder={t('timesheet.bonus-value')}
                  onChange={handleChange}
                  name={
                    state.useValuesFromContract
                      ? 'bonusRateFromContract'
                      : 'bonusRate'
                  }
                  value={String(
                    state.useValuesFromContract
                      ? state.bonusRateFromContract
                      : state.bonusRate || '',
                  )}
                />
                <FieldsetInput
                  label={t('timesheet.reward')}
                  type="number"
                  step={0.01}
                  placeholder={t('timesheet.reward-value')}
                  onChange={handleChange}
                  name={
                    state.useValuesFromContract
                      ? 'rewardRateFromContract'
                      : 'rewardRate'
                  }
                  value={String(
                    state.useValuesFromContract
                      ? state.rewardRateFromContract
                      : state.rewardRate || '',
                  )}
                />
                <FieldsetSelect
                  label=""
                  name={
                    state.useValuesFromContract
                      ? 'bonusRateFromContractOption'
                      : 'bonusRateOption'
                  }
                  value={
                    state.useValuesFromContract
                      ? state.bonusRateFromContractOption.toString()
                      : state.bonusRateOption.toString()
                  }
                  options={BONUS_RATES_OPTIONS.map((o) => ({
                    key: o,
                    value: o.toString(),
                    label: getRatesOptionName(o, t),
                  }))}
                  handleChange={handleSelect}
                />
                <FieldsetSwitch
                  label={t('timesheet.according-to-contract')}
                  checked={state.useValuesFromContract}
                  onChange={toggleUseValuesFromContract}
                  className="tw-flex-row tw-items-center tw-justify-between"
                />
              </div>
              {timesheetAdditionForLeaves.length > 0 ? (
                <div className="tw-my-2">
                  <h4 className="tw-text-md tw-pb-2 tw-font-semibold">
                    {t(
                      'changeBonusConfigurationPopup.timesheetAdditionForLeaves',
                    )}
                  </h4>
                  <Table>
                    {timesheetAdditionForLeaves.map((a, i) => {
                      return (
                        <TableRow key={i} className="tw-mb-1">
                          <TableCell flex={2}>
                            {getDayTypeName(a.dayType, t)}
                          </TableCell>
                          <TableCell flex={1}>
                            <FieldsetInput
                              label=""
                              type="number"
                              step={0.01}
                              placeholder={t('common.value')}
                              onChange={handleChangeAddition}
                              name={String(a.dayType)}
                              value={String(a.amount)}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </Table>
                </div>
              ) : null}

              {state && Array.isArray(state.additionalBonuses) ? (
                <div className="tw-my-2">
                  <Table>
                    <TableRow className="tw-mb-2">
                      <TableCell flex={TABLE_ALLOWANCES_COLSPAN.NAME}>
                        <h4 className="tw-pb-2 tw-text-lg tw-font-semibold">
                          Dodatki
                        </h4>
                      </TableCell>
                      <TableCell flex={TABLE_ALLOWANCES_COLSPAN.QUANTITY}>
                        Kwota
                      </TableCell>
                      <TableCell flex={TABLE_ALLOWANCES_COLSPAN.RATE}>
                        Ilość
                      </TableCell>
                      <TableCell flex={TABLE_ALLOWANCES_COLSPAN.TOTAL}>
                        Wartość
                      </TableCell>
                    </TableRow>
                    {state.additionalBonuses.map((row, rowIndex) => {
                      const enumValue = mapTimesheetAdditionalBonusApiToEnum(
                        row.bonusType,
                      )
                      const value = Number(row.quantity) * Number(row.bonusRate)

                      return (
                        <TableRow
                          key={`${row.bonusType}:${enumValue}:${rowIndex}`}
                          testId={`${row.bonusType}:${enumValue}:${row.bonusName}`}
                        >
                          <TableCell flex={TABLE_ALLOWANCES_COLSPAN.NAME}>
                            {row.bonusName ||
                              getTimesheetAdditionalBonusName(enumValue, t)}
                          </TableCell>
                          <TableCell flex={TABLE_ALLOWANCES_COLSPAN.QUANTITY}>
                            <FieldsetInput
                              label=""
                              type="number"
                              step={0.01}
                              placeholder="kwota"
                              onChange={handleChangeAllowances}
                              name={'bonusRate'}
                              data-bonus-type={row.bonusType}
                              data-index={rowIndex}
                              value={String(row.bonusRate)}
                              // disabled={state.useValuesFromContract}
                            />
                          </TableCell>
                          <TableCell flex={TABLE_ALLOWANCES_COLSPAN.RATE}>
                            <FieldsetInput
                              label=""
                              type="number"
                              step={1}
                              placeholder="ilość"
                              onChange={handleChangeAllowances}
                              name={'quantity'}
                              data-bonus-type={row.quantity}
                              data-index={rowIndex}
                              value={String(row.quantity)}
                              // disabled={state.useValuesFromContract}
                            />
                          </TableCell>
                          <TableCell flex={TABLE_ALLOWANCES_COLSPAN.TOTAL}>
                            <FieldsetInput
                              label=""
                              type="number"
                              step={0.01}
                              placeholder="wartość"
                              value={String(value)}
                              disabled={true}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    <TableRow>
                      <TableCell flex={TABLE_ALLOWANCES_COLSPAN.ALL}>
                        <label className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                          <Checkbox
                            value={
                              state[TABLE_ALLOWANCES_CHECKBOX_NAME] || false
                            }
                            name={TABLE_ALLOWANCES_CHECKBOX_NAME}
                            onChange={handleChangeAllowances}
                          />
                          <span>{t(TABLE_ALLOWANCES_CHECKBOX_KEY)}</span>
                        </label>
                      </TableCell>
                    </TableRow>
                  </Table>
                </div>
              ) : null}

              <div className="tw-mt-4 tw-border-t tw-border-[#2448bb]/40 tw-pt-4">
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 md:tw-items-end md:tw-gap-4">
                  <FieldsetInput
                    label={t('timesheet.finalLeavesGrossHours')}
                    type="number"
                    step={0.01}
                    placeholder={t('common.value')}
                    onChange={handleChange}
                    name="finalLeavesGrossHours"
                    value={
                      state.finalLeavesGrossHours != null
                        ? String(state.finalLeavesGrossHours)
                        : ''
                    }
                  />
                  <FieldsetInput
                    label={t('timesheet.finalLeavesGrossAmount')}
                    type="number"
                    step={0.01}
                    placeholder={t('common.value')}
                    onChange={handleChange}
                    name="finalLeavesGrossAmount"
                    suffix={t('timesheet.plnSuffix')}
                    value={
                      state.finalLeavesGrossAmount != null
                        ? String(state.finalLeavesGrossAmount)
                        : ''
                    }
                  />
                </div>
              </div>

              <div className="tw-my-2 tw-flex tw-justify-end tw-gap-2">
                <Button
                  text="Restart"
                  className="tw-h-[38px]"
                  variant="error"
                  onClick={handleCancel}
                  disabled={!isDirty || isSaving}
                />
                <Button
                  text={t('operation.save')}
                  className="tw-h-[38px]"
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!isDirty || isSaving}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </ModalLayout>
  )
}
